import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Switch,
  useMediaQuery,
  useTheme,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Edit,
  Delete,
  FileDownload,
  Add,
  History,
  Search,
  Image as ImageIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import axios from "axios";
import debounce from "lodash.debounce";

import config from "../../config";
import useLayoutStore from "../../store/useLayoutStore";
import CustomTable from "../../components/common/CustomTable";
import CustomPaging from "../../components/common/CustomPaging";
import CustomButton from "../../components/common/CustomButton";

const drawerWidth = 240;
const collapsedWidth = 80;

// CmsForm Component - ĐÃ SỬA LỖI
const CmsForm = ({
  open,
  onClose,
  data,
  onChange,
  onSubmit,
  previewUrl,
  setPreviewUrl,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  // Đảm bảo data không bao giờ là null
  const formData = data || {};

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      onChange("image", file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
    >
      <DialogTitle>
        {formData.id ? t("Edit CMS") : t("Create New CMS")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t("Title")}
            value={formData.title || ""}
            onChange={(e) => onChange("title", e.target.value)}
            fullWidth
          />
          <TextField
            label={t("Sub Title")}
            value={formData.subTitle || ""}
            onChange={(e) => onChange("subTitle", e.target.value)}
            fullWidth
          />
          <TextField
            label={t("Description")}
            value={formData.description || ""}
            onChange={(e) => onChange("description", e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label={t("Type")}
            value={formData.type || ""}
            onChange={(e) => onChange("type", e.target.value)}
            fullWidth
          />
          <TextField
            label={t("Order")}
            type="number"
            value={formData.displayOrder || ""}
            onChange={(e) =>
              onChange("displayOrder", parseInt(e.target.value) || 0)
            }
            fullWidth
          />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Switch
              checked={formData.isActive || false}
              onChange={(e) => onChange("isActive", e.target.checked)}
            />
            <Typography>{t("Active")}</Typography>
          </Box>

          <Box>
            <Button
              variant="outlined"
              component="label"
              startIcon={<ImageIcon />}
              fullWidth={isMobile}
            >
              {t("Upload Image")}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageChange}
              />
            </Button>
            {previewUrl && (
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "200px",
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button onClick={onSubmit} variant="contained">
          {formData.id ? t("Update") : t("Create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// LogsDialog Component
const LogsDialog = ({ open, logs, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
    >
      <DialogTitle>{t("CMS Logs")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {logs.map((log, index) => (
            <Box
              key={index}
              sx={{ p: 2, border: "1px solid #ddd", borderRadius: 1 }}
            >
              <Typography variant="body2">
                <strong>{log.action}</strong> by {log.user} at{" "}
                {new Date(log.timestamp).toLocaleString()}
              </Typography>
              {log.details && (
                <Typography variant="caption" color="text.secondary">
                  {JSON.stringify(log.details)}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

const CmsList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isSidebarOpen } = useLayoutStore();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // ✅ Chiều rộng sidebar theo từng màn hình
  let drawerW = 0;
  if (isMobile) {
    drawerW = 0;
  } else if (isTablet) {
    drawerW = 200;
  } else {
    drawerW = isSidebarOpen ? drawerWidth : collapsedWidth;
  }

  const [cmsList, setCmsList] = useState([]);
  const [allCmsData, setAllCmsData] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [typeOptions, setTypeOptions] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(isMobile ? 5 : 10);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // SỬA: Đảm bảo data không bao giờ là null
  const [editDialog, setEditDialog] = useState({
    open: false,
    data: {},
    image: null,
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [logsDialog, setLogsDialog] = useState({ open: false, logs: [] });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchTypes = useCallback(async () => {
    try {
      const res = await axios.get(`${config.apiBaseUrl}/cms`);
      const types = [...new Set(res.data.map((c) => c.type))];
      setTypeOptions(types);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchCms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${config.apiBaseUrl}/cms`, {
        params: { type: typeFilter },
      });
      const filtered = res.data.filter(
        (c) =>
          !searchQuery ||
          c.title?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setAllCmsData(filtered);
      const start = (page - 1) * itemsPerPage;
      const paginated = filtered.slice(start, start + itemsPerPage);
      setCmsList(paginated);
    } catch (error) {
      console.error(error);
      showSnackbar(t("Failed to load CMS data"), "error");
    } finally {
      setLoading(false);
    }
  }, [page, itemsPerPage, typeFilter, searchQuery, t]);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);
  useEffect(() => {
    setPage(1);
  }, [typeFilter, searchQuery]);
  useEffect(() => {
    const debounceFetch = debounce(fetchCms, 400);
    debounceFetch();
    return () => debounceFetch.cancel();
  }, [fetchCms]);

  const handleEditSubmit = async () => {
    const { data, image } = editDialog;
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) =>
      formData.append(key, value ?? ""),
    );
    if (image) formData.append("image", image);

    try {
      if (data.id) {
        await axios.put(`${config.apiBaseUrl}/cms/${data.id}`, formData);
        showSnackbar(t("CMS updated successfully"));
      } else {
        await axios.post(`${config.apiBaseUrl}/cms`, formData);
        showSnackbar(t("CMS created successfully"));
      }
      setEditDialog({ open: false, data: {}, image: null });
      setPreviewUrl(null);
      fetchCms();
    } catch (error) {
      console.error(error);
      showSnackbar(t("Failed to save CMS"), "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("Are you sure you want to delete this CMS?"))) return;
    try {
      await axios.delete(`${config.apiBaseUrl}/cms/${id}`);
      showSnackbar(t("CMS deleted successfully"));
      fetchCms();
    } catch (error) {
      console.error(error);
      showSnackbar(t("Failed to delete CMS"), "error");
    }
  };

  const handleViewLogs = async (cmsId) => {
    try {
      const res = await axios.get(`${config.apiBaseUrl}/cmslog/${cmsId}`);
      setLogsDialog({ open: true, logs: res.data });
    } catch (error) {
      console.error(error);
      showSnackbar(t("Failed to load logs"), "error");
    }
  };

  const exportToExcel = () => {
    const exportData = allCmsData.map((item) => ({
      ID: item.id,
      [t("Title")]: item.title,
      [t("Sub Title")]: item.subTitle,
      [t("Description")]: item.description,
      [t("Type")]: item.type,
      [t("Order")]: item.displayOrder,
      [t("Active")]: item.isActive ? t("Yes") : t("No"),
      [t("Created At")]: new Date(item.createdAt).toLocaleDateString("vi-VN"),
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CmsList");
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer]), "DanhSachCMS.xlsx");
    showSnackbar(t("Exported to Excel"));
  };

  const cmsColumns = [
    {
      field: "id",
      label: "ID",
      minWidth: isMobile ? "60px" : "80px",
      hideOnMobile: false,
    },
    {
      field: "title",
      label: t("Title"),
      minWidth: isMobile ? "120px" : "180px",
      hideOnMobile: false,
    },
    {
      field: "type",
      label: t("Type"),
      minWidth: isMobile ? "80px" : "100px",
      hideOnMobile: true,
    },
    {
      field: "displayOrder",
      label: t("Order"),
      minWidth: isMobile ? "60px" : "80px",
      hideOnMobile: true,
    },
    {
      field: "isActive",
      label: t("Active"),
      minWidth: isMobile ? "80px" : "100px",
      customRender: (row) => (
        <Chip
          label={row.isActive ? t("Yes") : t("No")}
          color={row.isActive ? "success" : "default"}
          size="small"
        />
      ),
      hideOnMobile: true,
    },
    {
      field: "createdAt",
      label: t("Created At"),
      minWidth: isMobile ? "100px" : "150px",
      customRender: (row) =>
        new Date(row.createdAt).toLocaleDateString("vi-VN"),
      hideOnMobile: true,
    },
    {
      field: "actions",
      label: t("Actions"),
      minWidth: isMobile ? "120px" : "150px",
      customRender: (row) => (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          <Tooltip title={t("Edit CMS")}>
            <IconButton
              size="small"
              onClick={() => {
                setEditDialog({ open: true, data: row, image: null });
                setPreviewUrl(row.imageUrl || null);
              }}
            >
              <Edit fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("Delete CMS")}>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(row.id)}
            >
              <Delete fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("View Logs")}>
            <IconButton size="small" onClick={() => handleViewLogs(row.id)}>
              <History fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      hideOnMobile: false,
    },
  ];

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: isMobile ? 1 : 2,
        width: "100%",
        minWidth: 0,
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
          {t("CMS Management")}
        </Typography>
        <CustomButton
          text={isMobile ? t("Create") : t("Create New CMS")}
          startIcon={<Add />}
          onClick={() => setEditDialog({ open: true, data: {}, image: null })}
          size={isMobile ? "small" : "medium"}
        />
      </Box>

      {/* Search and Filters */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ mb: 2 }}
      >
        <TextField
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t("Search CMS...")}
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 250, md: 300 },
            width: { xs: "100%", sm: "auto" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl
          sx={{
            minWidth: { xs: "100%", sm: 180, md: 200 },
            width: { xs: "100%", sm: "auto" },
          }}
          size="small"
        >
          <InputLabel>{t("Type")}</InputLabel>
          <Select
            value={typeFilter}
            label={t("Type")}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <MenuItem value="">{t("All")}</MenuItem>
            {typeOptions.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<FileDownload />}
          onClick={exportToExcel}
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: "auto" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {isMobile ? t("Export") : t("Export Excel")}
        </Button>
      </Stack>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 5, flex: 1 }}>
          <CircularProgress />
        </Box>
      ) : cmsList.length > 0 ? (
        <Box sx={{ flex: 1, minWidth: 0, overflowX: "auto" }}>
          <CustomTable
            columns={cmsColumns}
            data={cmsList}
            isMobile={isMobile}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
          }}
        >
          <Typography align="center">
            {searchQuery || typeFilter
              ? t("No CMS data found matching your criteria")
              : t("No CMS data available")}
          </Typography>
        </Box>
      )}

      {/* Pagination */}
      {allCmsData.length > 0 && (
        <CustomPaging
          page={page}
          setPage={setPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          totalItems={allCmsData.length}
          isMobile={isMobile}
        />
      )}

      {/* Dialogs */}
      <CmsForm
        open={editDialog.open}
        onClose={() => {
          setEditDialog({ open: false, data: {}, image: null });
          setPreviewUrl(null);
        }}
        data={editDialog.data}
        onChange={(key, value) =>
          setEditDialog((prev) => ({
            ...prev,
            data: { ...prev.data, [key]: value },
          }))
        }
        onSubmit={handleEditSubmit}
        previewUrl={previewUrl}
        setPreviewUrl={setPreviewUrl}
      />

      <LogsDialog
        open={logsDialog.open}
        logs={logsDialog.logs}
        onClose={() => setLogsDialog({ open: false, logs: [] })}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{
          vertical: isMobile ? "bottom" : "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CmsList;
